import {Trigger} from "./interfaces/Trigger";
import {ObjectTriggerTypes, ProjectLinkModes, VisibilityModes, BillboardModes, TextAlign} from "./interfaces/enums";
import { BillboardSettings } from "./interfaces/Billboard";
import { Pivot, PivotSpace } from "./interfaces/Pivot";
import { Annotation } from "./interfaces/Annotation";
import { IObjectPartCatalog } from "./interfaces/PartsCatalog";

export enum RenderModes {
	Standalone = "standalone",
	Device = "device",
}

export enum CameraProjectionModes {
	Perspective = "perspective",
	Orthographic = "orthographic",
}

export enum ApplicationModuleTypes {
	SceneView = "SceneView",
	CameraPreview = "CameraPreview",
	SequenceEditor = "SequenceEditor",
	ActionHistory = "ActionHistory",
	ObjectProperties = "ObjectProperties",
	StepHierarchy = "StepHierarchy",
	TimelineEditor = "TimelineEditor",
	AssetLibrary = "AssetLibrary",
	UIEditor = "UIEditor",
	ObjectGroups = "ObjectGroups",
	ChecklistEditor = "ChecklistEditor",
	ChatGPTEditor = "ChatGPTEditor",
	ChatGPTPromptEditor = "ChatGPTPromptEditor",
	AIAssistant = "AIAssistant",
	FormEditor = "FormEditor",
	RevisionsEditor = "RevisionsEditor",
	ARTrackers = "ARTrackers",
	Keybindings = "Keybindings",
	Export = "Export",
	IoT = "IoT",
	StepConditions = "StepConditions",
	Attachments = "Attachments",
	VisageEditorWindow = "VisageEditorWindow",
	StepActions = "StepActions",
	ProjectHealth = "ProjectHealth",
	PartsCatalogEditor = "PartsCatalogEditor",
	PreferencesWindow = "PreferencesWindow",
	ProjectSettingsWindow = "ProjectSettingsWindow",
}

export const WindowDisplayModes = {
	Collapsed: "Collapsed",
	Expanded: "Expanded",
};

export const DimensionModes = {
	Percentage: "Percentage",
	Absolute: "Absolute",
};

export const NativeElementTags = [
	"INPUT",
	"TEXTAREA",
	//"IFRAME",
];

export enum SimulationStates {
	Playing,
	Paused,
	Stopped,
}

export enum LayoutOrientations {
	Vertical = "vertical",
	Horizontal = "horizontal",
}

export const RenderLayers = {
	Default: 0,
	MainCameraOnly: 1,
	ModelTrackerPreview: 2,
	ObjectPreview: 3,
	GridLayer: 4,
};

export const FocusChange = {
	FocusGained: "FocusGained",
	FocusLost: "FocusLost",
};

export enum TrackerTypes {
	SLAM = "slam",
	IMAGE = "image-target",
	QR = "qr",
	MODEL = "model",
}

export const ChecklistItemMaxOptions = 26;

export enum ChecklistType {
	TEXT = "text",
	CHECKBOX = "check-box",
	NUMBER = "number",
	PHOTO = "photo",
	MULTI_OPTION = "multi-option",
	MULTI_OPTION_SELECT = "multi-option-select",
	MULTI_OPTION_3D = "multi-option-3d",
	MULTI_OPTION_SELECT_3D = "multi-option-select-3d",
	YESNO = "yes-no-option", //deprecated -> will convert to multioption on load
	BARCODE = 'barcode',
}

export enum ChecklistFormQuestionType {
	TEXT = "text",
	CHECKBOX = "check-box",
	NUMBER = "number",
	PHOTO = "photo",
	MULTI_OPTION = "multi-option",
	MULTI_OPTION_SELECT = "multi-option-select",
	YESNO = "yes-no-option", //deprecated -> will convert to multioption on load
	BARCODE = 'barcode',
}

export enum ChecklistQuizQuestionType {
	MULTI_OPTION = "multi-option",
	MULTI_OPTION_SELECT = "multi-option-select",
	MULTI_OPTION_3D = "multi-option-3d",
	MULTI_OPTION_SELECT_3D = "multi-option-select-3d"
}

export enum ChecklistPriority {
	FORCEVIEW = "force-view",
	PASSIVE = 'passive',
	MUSTRESPOND = 'must-respond',
	AUTOOPEN = "AUTOOPEN",
}

export const ChecklistEvents = {
	START: "step-start",
	END: "step-end",
	TIMELIME: 'timeline-trigger',
	BUTTON: 'button',
	AUTOOPEN: 'auto-open',
};

export enum ChecklistFormType {
	NONE = "none",
	FORM = "form",
	QUIZ = 'quiz',
}

export enum QuizType {
	GRADED = "graded-assessment",
	UNGRADED =  'knowledge-check',
}

export enum ReviewQuiz {
	OFF = "review-quiz-off",
	ON = 'review-quiz-on',
	UNGRADED = "review-quiz-ungraded-only",
	GRADED = 'review-quiz-graded-only',
}

export enum AllowRetriesOptions {
	NEVER = "never",
	FAIL_ONLY = 'fail-only',
	PASS_OR_FAIL = "pass-or-fail",
}

export enum QuizRevealAnswers {
	NEVER = "reveal-never",
	ALWAYS = "reveal-always",
	GRADED = "reveal-graded-only",
	UNGRADED = "reveal-ungraded-only",
}

export enum QuizQuestionStatus {
	UNANSWERED = "unanswered",
	CORRECT = 'correct',
	INCORRECT = 'incorrect',
}

export const QuizAnswerStatusColor = {
	CORRECT: "#26BB66",
	INCORRECT: '#EB5757',
};

export const defaultQuizSettings = {
	type: QuizType.UNGRADED,
	passingScore: 80,
	gradedAttempts: 1,
	practiceSessions: true,
	leaderboards: true,
	reviewQuiz: ReviewQuiz.UNGRADED,
	shuffleQuestions: true,
	revealAnswers: QuizRevealAnswers.ALWAYS,
	allowRetries: AllowRetriesOptions.NEVER,
};

export const RevisionDialogMode = {
	SIMPLE: 0,
	ADVANCED: 1,
};

export const RevisionStatusType = {
	PENDING: 'queued',
	PROGRESS: 'processing',
	PROCESSING_CAD: 'processing CAD',
	PENDING_RENDER: 'pending render',
	RENDERING: 'rendering',
	PENDING_LOCAL_RENDER: 'pending local render',
	LOCAL_RENDERING: 'local render',
	DONE: 'success',
	FAILED: 'error',
};

export const RevisionType = {
	NONE: 'none',
	LOW: 'low',
	MEDIUM: 'medium',
	HIGH: 'high',
};



export enum PropertyTypes {
	Asset = "asset",
	Boolean = "bool",
	Camera = "camera",
	BoolSelectorRadio = "boolSelectorRadio",
	BoolSelector = "boolSelector",
	Checklist = "checklist",
	Color = "color",
	DropDown = "dropdown",
	Number = "number",
	SceneObject = "sceneObject",
	Sequence = "sequence",
	Slider = "slider",
	Step = "step",
	String = "string",
	Text = "text",
	Vector2 = "vector2",
	Vector3 = "vector3",
	ARTracker = "ARTracker",
	ImageSelector = "imageSelector",
}

export enum ContentAnchors  {
	None = "",
	UpperLeft = "upperLeft",
	UpperCenter = "upperCenter",
	UpperRight = "upperRight",
	MiddleLeft = "middleLeft",
	MiddleCenter = "middleCenter",
	MiddleRight = "middleRight",
	LowerLeft = "lowerLeft",
	LowerCenter = "lowerCenter",
	LowerRight = "lowerRight",
	Left = "Left",
	Center = "Center",
	Right = "Right",
	Upper = "upper",
	Middle = "middle",
	Lower = "lower",
	Free = "free",
}

export enum CustomEventTypes {
	// timeline
	timelineRefersh = "timelineRefersh",
	timelineRecordStarted = "timelineRecordStarted",
	timelineRecordStoped = "timelineRecordStoped",
	timelineDataLoaded = "timelineDataLoaded",
	timelinePlaybackStarted = "timelinePlaybackStarted",
	timelinePlaybackStoped = "timelinePlaybackStoped",
	timelinePropertyUpdated = "timelinePropertyUpdated",

	// application
	applicationFocusChanged = "applicationFocusChanged",
	destroyModalRequested = "destroyModalRequested",
	modalsUpdated = "modalsUpdated",
	simulationStarted = "simulationStarted",
	simulationStopped = "simulationStopped",
	windowMoved = "windowMoved",
	windowClosed = "windowClosed",
	layoutChanged = "layoutChanged",
	dropdownChanged = "dropDownChanged",
	notificationsUpdated = "notificationsUpdated",
	FPSUpdated = "FPSUpdated",
	onModalOpened = "onModalOpened",
	onModalClosed = "onModalClosed",
	onUndo = "onUndo",
	onRedo = "onRedo",
	keybindingChanged = "keybindingChanged",
	saveWlcSettings = "saveWlcSettings",

	// projects
	onProjectLoadStart = "onProjectLoadStart",
	onProjectLoadEnd = "onProjectLoadEnd",
	onProjectSaveStart = "onProjectSaveStart",
	onProjectSaveEnd = "onProjectSaveEnd",
	onElementNameChanged = "onElementNameChanged",

	// gizmos
	onGizmoChanged = "onGizmoChanged",
	onGizmoSpaceChanged = "onGizmoSpaceChanged",
	onGizmoPivotModeChanged = "onGizmoPivotModeChanged",
	onGizmoFreeMoveModeChanged = "onGizmoFreeMoveModeChanged",

	// annotations
	annotationUpdated = "annotationUpdated",

	// hierarchy
	hierarchyChangedEvent = "hierarchyChangedEvent",

	// camera
	cameraProjectionModeChanged = "cameraProjectionModeChanged",

	// objects
	objectNameChanged = "objectNameChanged",
	objectSelectionChanged = "objectSelectionChanged",
	selectedObjectPositionChanged = "selectedObjectPositionChanged",
	selectedObjectRotationChanged = "selectedObjectRotationChanged",
	selectedObjectScaleChanged = "selectedObjectScaleChanged",
	selectedObjectAnimated = "selectedObjectAnimated",
	visibilityModeChanged = "VisibilityModeChanged",
	onTransformRecordEvent = "onTransformRecordEvent",
	renderModeChanged = "renderModeChanged",
	onObjectDeleted = "onObjectDeleted",
	objectManualHiddenStateChanged = "objectManualHiddenStateChanged",
	objectMetadataValueChanged = "objectMetadataValueChanged",

	// engine
	viewTriCountChanged = "viewTriCountChanged",
	cameraRendered = "cameraRendered",
	onDisplayUnitChange = "onDisplayUnitChange",
	onFontLoaded = "onFontLoaded",
	onImageTargetTextureLoaded = "onImageTargetTextureLoaded",
	onInputUpdated = "onInputUpdated",
	onDoubleShift = "onDoubleShift",
	onUnloadScene = "onUnloadScene",

	// groups
	onGroupListUpdated = "onGroupListUpdated",

	// AR trackers
	onTrackerListUpdated = "onTrackerListUpdated",
	onTrackerPreviewUpdated = "onTrackerPreviewUpdated",

	//UI Components updaters
	updateAudioComponent = "updateAudioComponent",
	updateCameraComponent = "updateCameraComponent",
	updateLightComponent = "updateLightComponent",
	update3DTextComponent = "update3DTextComponent",
	updateTrackerComponent = "updateTrackerComponent",
	updateTriggerComponent = "updateTriggerComponent",
	updatePivotComponent = "updatePivotComponent",
	updateBillboardComponent = "updateBillboardComponent",
	updateCalloutComponent = "updateCalloutComponent",
	updateMaterialComponent = "updateMaterialComponent",
	updatePropertiesComponent = "updatePropertiesComponent",
	updateMetadataComponent = "updateMetadataComponent",
	updateObjectModified = "updateObjectModified",
	updatePartsCatalogComponent = "updatePartsCatalogComponent",

	//Forms / Quizes
	toggleQuizAnswer = "toggleQuizAnswer",
	onPrefillValidationFailed = "onPrefillValidationFailed",

	//IOT
	onIOTValueUpdated = "onIOTValueUpdated",

	// grapesjs
	grapesEditorInitialized = "grapesEditorInitialized",
	grapesImageSet = "grapesImageSet",
	gjsComponentUpdated = "onGjsComponentUpdated",
	gjsComponentDragStart = "onGjsComponentDragStart",
	gjsComponentDrag = "onGjsComponentDrag",
	gjsComponentDragEnd = "onGjsComponentDragEnd",
	gjsComponentClicked = "onGjsComponentClicked",
	gjsDeselectAll = "onGjsDeselectAll",

	//userflow
	onUserflowResourceCenterChanged = "onUserflowResourceCenterChanged",

	//Visage UI
	onSaveTemplateChanges = "onSaveTemplateChanges",
	onDiscardTemplateChanges = "onDiscardTemplateChanges",
	gjsContentEditEnd = "gjsContentEditEnd",
	gjsContentEditStart = "gjsContentEditStart",

	// AI Companion
	InstructionsModified = "InstructionsModified",
}

export const Alignment = {
	TOP: "top",
	BOTTOM: "bottom",
	LEFT: "left",
	RIGHT: "right",
	FRONT: "front",
	BACK: "back",
	CENTERX: "centerX",
	CENTERY: "centerY",
	CENTERZ: "centerZ"
};

export enum WorldUnitScales {
	Millimeters = 0.001,
	Centimeters = 0.01,
	Inches = 0.0254,
	Feet = 0.3048,
	Yards = 0.9144,
	Meters = 1,
}

export enum PrimitiveTypes {
	Cube = "Cube",
	Plane = "Plane",
	Sphere = "Sphere",
	Cylinder = "Cylinder",
}

export enum SelectionType {
	Child = "child",
	Parent = "parent",
}

export enum DndItemTypes {
	GENERIC_TREE_ITEM = "GENERIC_TREE_ITEM",
	ASSET_TREE_ITEM = "ASSET_TREE_ITEM",
	ASSET_PREVIEW_ITEM = 'ASSET_PREVIEW_ITEM',
	STEP_ITEM = "STEP_ITEM",
	SCENE_HIERARCHY_TREE_ITEM = "SceneHierarchyTreeItem",
	OBJECT_GROUP_TREE_ITEM = "OBJECT_GROUP_TREE_ITEM",
	CHECKLIST_ITEM = "ChecklistItem",
	CHECKLIST_OPTION_ITEM = "ChecklistOptionItem",
	ASSET_SWAP_NODE = "ASSET_SWAP_NODE",
	REVISION_OPERATION_ITEM = "REVISION_OPERATION_ITEM",
	SLIDE_SUBCOMPONENT_ITEM = "SLIDE_SUBCOMPONENT_ITEM",
	TIMELINE_CHANNEL = "TIMELINE_CHANNEL",
	TIMELINE_OBJECT = "TIMELINE_OBJECT",
	TRACKER_TREE_ITEM = "TRACKER_TREE_ITEM",
	SEQUENCE_TOPBAR_ITEM = "SEQUENCE_TOPBAR_ITEM",
	STEP_TOPBAR_ITEM = "STEP_TOPBAR_ITEM",
	STEP_SEQUENCE_EDITOR_ITEM = "STEP_SEQUENCE_EDITOR_ITEM",
	PARTS_CATALOG_METADATA_ITEM = "PARTS_CATALOG_METADATA_ITEM",
	VISAGE_HIERARCHY_TREE_ITEM = "VISAGE_HIERARCHY_TREE_ITEM",
}

//thanks bud https://gist.github.com/gre/1650294
export const EasingFunctions = {
	// no easing, no acceleration
	linear: (t:number) => t,
	// accelerating from zero velocity
	easeInQuad: (t:number) => t * t,
	// decelerating to zero velocity
	easeOutQuad: (t:number) => t * (2 - t),
	// acceleration until halfway, then deceleration
	easeInOutQuad: (t:number) => t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
	// accelerating from zero velocity
	easeInCubic: (t:number) => t * t * t,
	// decelerating to zero velocity
	easeOutCubic: (t:number) => (--t) * t * t + 1,
	// acceleration until halfway, then deceleration
	easeInOutCubic: (t:number) => t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
	// accelerating from zero velocity
	easeInQuart: (t:number) => t * t * t * t,
	// decelerating to zero velocity
	easeOutQuart: (t:number) => 1 - (--t) * t * t * t,
	// acceleration until halfway, then deceleration
	easeInOutQuart: (t:number) => t < .5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t,
	// accelerating from zero velocity
	easeInQuint: (t:number) => t * t * t * t * t,
	// decelerating to zero velocity
	easeOutQuint: (t:number) => 1 + (--t) * t * t * t * t,
	// acceleration until halfway, then deceleration
	easeInOutQuint: (t:number) => t < .5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t,
	// linear until halfway, then reverse
	pingPong: (t:number) => t < .5 ? t * 2 : (1 - t) * 2,
};

export enum OperatingSystems {
	Windows = "Windows",
	MacOS = "MacOS",
	IOS = "iOS",
	ANDROID = "Android",
	LINUX = "Linux",
	UNKNOWN = "Uknown OS"
}

export enum InputCategories {
	Reserved = "Reserved",
	Workspace = "Workspace",
	Window = "Window",
	TransformGizmo = "Transform Gizmo",
	CameraMovement = "Camera Movement",
	CameraUtilities = "Camera Utilities",
	Scenario = "Scenario",
	General = "General",
	Timeline = "Timeline",
	ObjectVisibility = "Object Visibility",
}

export enum EasingFunctionTypes  {
	Linear = "linear",
	EaseInQuad = "easeInQuad",
	EaseOutQuad = "easeOutQuad",
	EaseInOutQuad = "easeInOutQuad",
	EaseInCubic = "easeInCubic",
	EaseOutCubic = "easeOutCubic",
	EaseInOutCubic = "easeInOutCubic",
	EaseInOutQuart = "easeInOutQuart",
	EaseOutQuart = "easeOutQuart",
	EaseInOutQuint = "easeInOutQuint",
	PingPong = "pingPong",
}

export const EasingFilter = new Set([
	EasingFunctionTypes.EaseInCubic,
	EasingFunctionTypes.EaseOutCubic,
	EasingFunctionTypes.EaseInOutCubic,
	EasingFunctionTypes.EaseInOutQuart,
	EasingFunctionTypes.EaseOutQuart,
	EasingFunctionTypes.EaseInOutQuint,
]);

export const EasingTypeToFunctionMap = new Map([
	[EasingFunctionTypes.Linear, EasingFunctions.linear],
	[EasingFunctionTypes.EaseInQuad, EasingFunctions.easeInQuad],
	[EasingFunctionTypes.EaseOutQuad, EasingFunctions.easeOutQuad],
	[EasingFunctionTypes.EaseInOutQuad, EasingFunctions.easeInOutQuad],
	[EasingFunctionTypes.EaseInCubic, EasingFunctions.easeInCubic],
	[EasingFunctionTypes.EaseOutCubic, EasingFunctions.easeOutCubic],
	[EasingFunctionTypes.EaseInOutCubic, EasingFunctions.easeInOutCubic],
	[EasingFunctionTypes.EaseInOutQuart, EasingFunctions.easeInOutQuart],
	[EasingFunctionTypes.EaseOutQuart, EasingFunctions.easeOutQuart],
	[EasingFunctionTypes.EaseInOutQuint, EasingFunctions.easeInOutQuint],
	[EasingFunctionTypes.PingPong, EasingFunctions.pingPong],
]);

export const EasingTypeToCaptionMap = new Map([
	[EasingFunctionTypes.Linear, "Linear"],
	[EasingFunctionTypes.EaseInQuad, "Ease In"],
	[EasingFunctionTypes.EaseOutQuad, "Ease Out"],
	[EasingFunctionTypes.EaseInOutQuad, "Ease In Out"],
	[EasingFunctionTypes.EaseInCubic, "Ease In Cubic"],
	[EasingFunctionTypes.EaseOutCubic, "Ease Out Cubic"],
	[EasingFunctionTypes.EaseInOutCubic, "Ease In Out Cubic"],
	[EasingFunctionTypes.EaseInOutQuart, "Ease In Out Quart"],
	[EasingFunctionTypes.EaseOutQuart, "Ease Out Quart"],
	[EasingFunctionTypes.EaseInOutQuint, "Ease In Out Quint"],
	[EasingFunctionTypes.PingPong, "Ping Pong"],
]);

export enum GizmoModes{
	Translate = "translate",
	Rotate = "rotate",
	Scale = "scale",
	Hidden = "hidden"
}

export enum ToolTypes{
	DistanceTool =  "Distance Tool",
}

export enum ToolState
	{
	InitialPlacement = "InitialPlacement",
	Measure = "Measure",
	Remeasure = "Remeasure",
	Finalized = "Finalized"
}

// based on https://www.andiamo.co.uk/resources/iso-language-codes/
export enum Languages {
	EnglishUnitedStates = "en-us",
}

export enum MaterialTypes {
	//Diffuse = 'diffuse', deprecated
	DiffuseUnlit = 'diffuseUnlit',
	DepthMask = 'depthMask',
	Standard = 'standard',
	Video = 'video',
	Line = 'line',
}

export enum ButtonIconAnimations {
	RotateLeft = 'rotateLeft',
	RotateRight = 'rotateRight',
}

export enum SaveStates {
	Pending = "pending",
	InProgress = "progress",
	Success = "success",
	Error = "error",
}

export enum PublishTypes {
	persistent_for_any_user = 0,
	passive_session = 1,
	demo_session = 2,
	persistent_per_user = 3,
}

export const ValueTypes = Object.freeze({
	Scalar: "scalar",
	Vector2: "vector2",
	Vector3: "vector3",
	Quaternion: "quaternion",
	Color: "color",
});

export enum StepConnectionType {
	Headers = "Headers",
	Options = "Options",
	Buttons = "Buttons",
	Insets = "Insets",
}

export enum ExportType {
	NoAssets = "shallow",
	AssetsEmbedded = "processed only",
	Full = "deep",
}

export enum FadeType {
	fadeIn = "Fade In",
	fadeOut = "Fade Out",
}

export enum AlphaModes {
	Opaque = "Opaque",
	Blend = "Blend",
	Mask = "Mask"
}

export enum CreateProjectTypes{
	Empty = "Empty",
	Import = "From Import",
	Demo = "From Demo",
}

export enum LightTypes
	{
	Directional = "directional",
	Point = "point",
	Spot = "spot",
}

export const defaultTriggerObject: Trigger = {
	type: ObjectTriggerTypes.NextStep,
	targetStep: "",
	targetObject: "",
	targetObjectVisibility: VisibilityModes.Solid,
	projectLinkMode: ProjectLinkModes.Online,
	offlineScenarioName: "",
	onlineScenarioName: "",
	scenarioId: 0,
	calloutTrigger: false,
	url: "",
	customMaterial: "",
};

export const defaultPartsCatalogObject: IObjectPartCatalog = {
	included: true,
	isSpare: true,
	isAvailable: false,
	partsKitNumber: "",
};

export enum ObjectPartsCatalogActionKeys {
	INCLUDED = 'included',
	IS_SPARE = 'isSpare',
	IS_AVAILABLE = 'isAvailable',
	PARTS_KIT_NUMBER = 'partsKitNumber',
}

export const objectTriggerTypeNames = {
	[ObjectTriggerTypes.MoveToStep]: "Move To Step",
	[ObjectTriggerTypes.VisibilityToggle]: "Visibility Toggle",
	[ObjectTriggerTypes.CalloutToggle]: "Callout Toggle",
	[ObjectTriggerTypes.ProjectLink]: "Scenario Link",
	[ObjectTriggerTypes.URL]: "Open URL",
}

export const defaultBillboardObject: BillboardSettings = {
	enabled: false,
	mode: BillboardModes.Billboard,
	lockX: false,
	lockY: false,
	lockZ: false,
	offsetRotation: { x: 0, y: 0, z: 0 },
	forceRotateAboutCenter: false,
	targetGuid: "",
};

export const defaultPivot: Pivot = {
	displayOffset: { x: 0, y: 0, z: 0 },
	relativeOffset: { x: 0, y: 0, z: 0 },
	geometryOffset: { x: 0, y: 0, z: 0 },
	space: PivotSpace.Relative,
};

export enum ObjectInspectorSectionTypes {
	Audio = 'audio',
	Billboarding = 'billboarding',
	Callout = 'callout',
	Camera = 'camera',
	Light = 'light',
	Material = 'material',
	ObjectProperties = 'object-properties',
	ObjectTrigger = 'object-trigger',
	Pivot = 'pivot',
	Text3D = 'text3d',
	Tracker = 'tracker',
	Transform = 'transform',
}

export const InsetTypes = {
	INVISIBLE: "invisible",
	IMAGE: "image",
	STEP: "step",
	VIDEO: "video",
	AUDIO: "audio",
	URL: "url",
	CAMERA: "camera",
};

export const cssWindowPaddingAmount = 8;

export enum SaveModificationTypes {
	Position = "Position",
	Rotation = "Rotation",
	Scale = "Scale",
	Pivot = "Pivot",
	Name = "Name",
	Visibility = "Visibility",
	Material = "Material",
	Callout = "Callout",
	Trigger = "Trigger",
	Reference = "Reference",
	Billboard = "Billboard",
	Text3D = "Text3D",
	Light = "Light",
	Camera = "Camera",
	Audio = "Audio",
	Groups = "Groups",
	Removed = "Removed",
	ReAdded = "ReAdded",
	ReorderedChildren = "ReorderedChildren",
	Metadata = "Metadata",
	PartsCatalog = "PartsCatalog",
}

export enum Space{
	Local = "local",
	World = "world",
}

export enum GizmoPivotMode {
	Object = "object",
	Group = "group",
}

export const UnitToShortLabel = {
	[WorldUnitScales.Millimeters]: "mm",
	[WorldUnitScales.Centimeters]: "cm",
	[WorldUnitScales.Meters]: "m",
	[WorldUnitScales.Inches]: "in",
	[WorldUnitScales.Feet]: "ft",
	[WorldUnitScales.Yards]: "yd",
};

export enum ZoomBehaviors {
	CameraForward = "Camera Forward",
	TowardsCursor = "Towards Cursor",
	TowardsTouchCenter = "Towards Touch Center",
}

export enum FileIOType {
	Download = "download",
	Upload = "upload",
	Import = "import",
}

export enum IconSelectionStyles {
	outline = "outline",
}

export const SyncStatuses = Object.freeze({
	InSync: "InSync",
	OutOfSync: "OutOfSync",
	Mixed: "Mixed",
	Null: "Null",
});

export enum SortOrders {
	None,
	Ascending,
	Descending,
}

export enum AssetTypes {
	Material = "material",
	Revision = "revision",
	Model = "model",
	Folder = "folder",
	Image = "image",
	Video = "video",
	Audio = "audio",
	UITemplate = "slide",
	ModelProcessed = "processed_model",
	RevisionProcessed = "processed_revision",
	Document = "document",
	VisageTemplate = "visage_template",
}

export enum UISidebarContentTypes {
	None = "",
	Form = "form",
	Attachments = "attachments",
	PartsCatalog = "partsCatalog",
}

export enum PivotLocations {
	Center = "Center",
	Top = "Top",
	Bottom = "Bottom",
	Left = "Left",
	Right = "Right",
	Front = "Front",
	Back = "Back",
	TopBack = "TopBack",
	TopFront = "TopFront",
	TopLeft = "TopLeft",
	TopRight = "TopRight",
	BottomBack = "BottomBack",
	BottomFront = "BottomFront",
	BottomLeft = "BottomLeft",
	BottomRight = "BottomRight",
	BackLeft = "BackLeft",
	BackRight = "BackRight",
	FrontLeft = "FrontLeft",
	FrontRight = "FrontRight",
	TopBackLeft = "TopBackLeft",
	TopBackRight = "TopBackRight",
	TopFrontLeft = "TopFrontLeft",
	TopFrontRight = "TopFrontRight",
	BottomBackLeft = "BottomBackLeft",
	BottomBackRight = "BottomBackRight",
	BottomFrontLeft = "BottomFrontLeft",
	BottomFrontRight = "BottomFrontRight",
}

export enum CalloutWidthSettings{
	Fixed = "Fixed",
	Auto = "Auto",
}

export const defaultAnnotationObject: Annotation = {
	enabled: true,
	isVisible: true,
	title: "Title",
	scale: 1,
	autoWidth: true,
	width: 1,
	description: "Text",
	titleColor: { r: 0, g: 0, b: 0 },
	textColor: { r: 255, g: 255, b: 255 },
	titleBackground: { r: 255, g: 255, b: 255, a: 100 },
	backgroundColor: { r: 0, g: 0, b: 0, a: 100 },
	lineColor: { r: 0, g: 0, b: 0, a: 100 },
	offset: { x: 0.3, y: 0.3, z: 0 },
	originOffset: { x: 0, y: 0, z: 0},
	titleTextAlign: TextAlign.Left,
	descriptionTextAlign: TextAlign.Left,
	descriptionEnabled: true,
	strokeEnabled: false,
	strokeColor: { r: 0, g: 0, b: 0, a: 100 },
};

export enum ObjectClasses {
	Audio = "Audio",
	Video = "Video",
	Image = "Image",
	Light = "Light",
	Camera = "Camera",
	Model = "Model",
	Text3D = "Text3D",
	Empty = "Empty",
}

export enum TimelineEffectorTypes {
	EventInvocation = "eventInvocation",
	ObjectColor = "objectColor",
	ObjectFade = "objectFade",
	HighlightSphere = "highlightSphere",
	ObjectAnimation = "objectAnimation",
	TransformTranslate = "transformTranslate",
	TransformRotate = "transformRotate",
	TransformRotateSelf = "transformRotateSelf",
	TransformRotatePivot = "transformRotatePivot",
	TransformScale = "transformScale",
	VideoPlayback = "videoPlayback",
	AudioPlayback = "audioPlayback",
}

export enum TimelineTrackTypes {
	ActionsTrack = "actionsTrack",
	ObjectsTrack = "ObjectsTrack",
}

export const MimeToFileExtension = {
	'audio/x-mpeg': 'mpega',
	'application/postscript': 'ps',
	'audio/x-aiff': 'aiff',
	'application/x-aim': 'aim',
	'image/x-jg': 'art',
	'video/x-ms-asf': 'asx',
	'audio/basic': 'ulw',
	'video/x-msvideo': 'avi',
	'video/x-rad-screenplay': 'avx',
	'application/x-bcpio': 'bcpio',
	'application/octet-stream': 'exe',
	'image/bmp': 'dib',
	'text/html': 'html',
	'application/x-cdf': 'cdf',
	'application/pkix-cert': 'cer',
	'application/java': 'class',
	'application/x-cpio': 'cpio',
	'application/x-csh': 'csh',
	'text/css': 'css',
	'application/msword': 'doc',
	'application/xml-dtd': 'dtd',
	'video/x-dv': 'dv',
	'application/x-dvi': 'dvi',
	'application/vnd.ms-fontobject': 'eot',
	'text/x-setext': 'etx',
	'image/gif': 'gif',
	'application/x-gtar': 'gtar',
	'application/x-gzip': 'gz',
	'application/x-hdf': 'hdf',
	'application/mac-binhex40': 'hqx',
	'text/x-component': 'htc',
	'image/ief': 'ief',
	'text/vnd.sun.j2me.app-descriptor': 'jad',
	'application/java-archive': 'jar',
	'text/x-java-source': 'java',
	'application/x-java-jnlp-file': 'jnlp',
	'image/jpeg': 'jpg',
	'application/javascript': 'js',
	'text/plain': 'txt',
	'application/json': 'json',
	'audio/midi': 'midi',
	'application/x-latex': 'latex',
	'audio/x-mpegurl': 'm3u',
	'image/x-macpaint': 'pnt',
	'text/troff': 'tr',
	'application/mathml+xml': 'mathml',
	'application/x-mif': 'mif',
	'video/quicktime': 'qt',
	'video/x-sgi-movie': 'movie',
	'audio/mpeg': 'mpa',
	'video/mp4': 'mp4',
	'video/mpeg': 'mpg',
	'video/mpeg2': 'mpv2',
	'application/x-wais-source': 'src',
	'application/x-netcdf': 'nc',
	'application/oda': 'oda',
	'application/vnd.oasis.opendocument.database': 'odb',
	'application/vnd.oasis.opendocument.chart': 'odc',
	'application/vnd.oasis.opendocument.formula': 'odf',
	'application/vnd.oasis.opendocument.graphics': 'odg',
	'application/vnd.oasis.opendocument.image': 'odi',
	'application/vnd.oasis.opendocument.text-master': 'odm',
	'application/vnd.oasis.opendocument.presentation': 'odp',
	'application/vnd.oasis.opendocument.spreadsheet': 'ods',
	'application/vnd.oasis.opendocument.text': 'odt',
	'application/vnd.oasis.opendocument.graphics-template': 'otg',
	'application/vnd.oasis.opendocument.text-web': 'oth',
	'application/vnd.oasis.opendocument.presentation-template': 'otp',
	'application/vnd.oasis.opendocument.spreadsheet-template': 'ots',
	'application/vnd.oasis.opendocument.text-template': 'ott',
	'application/ogg': 'ogx',
	'video/ogg': 'ogv',
	'audio/ogg': 'spx',
	'application/x-font-opentype': 'otf',
	'audio/flac': 'flac',
	'application/annodex': 'anx',
	'audio/annodex': 'axa',
	'video/annodex': 'axv',
	'application/xspf+xml': 'xspf',
	'image/x-portable-bitmap': 'pbm',
	'image/pict': 'pict',
	'application/pdf': 'pdf',
	'image/x-portable-graymap': 'pgm',
	'audio/x-scpls': 'pls',
	'image/png': 'png',
	'image/x-portable-anymap': 'pnm',
	'image/x-portable-pixmap': 'ppm',
	'application/vnd.ms-powerpoint': 'pps',
	'image/vnd.adobe.photoshop': 'psd',
	'image/x-quicktime': 'qtif',
	'image/x-cmu-raster': 'ras',
	'application/rdf+xml': 'rdf',
	'image/x-rgb': 'rgb',
	'application/vnd.rn-realmedia': 'rm',
	'application/rtf': 'rtf',
	'text/richtext': 'rtx',
	'application/font-sfnt': 'sfnt',
	'application/x-sh': 'sh',
	'application/x-shar': 'shar',
	'application/x-stuffit': 'sit',
	'application/x-sv4cpio': 'sv4cpio',
	'application/x-sv4crc': 'sv4crc',
	'image/svg+xml': 'svgz',
	'application/x-shockwave-flash': 'swf',
	'application/x-tar': 'tar',
	'application/x-tcl': 'tcl',
	'application/x-tex': 'tex',
	'application/x-texinfo': 'texinfo',
	'image/tiff': 'tiff',
	'text/tab-separated-values': 'tsv',
	'application/x-font-ttf': 'ttf',
	'application/x-ustar': 'ustar',
	'application/voicexml+xml': 'vxml',
	'image/x-xbitmap': 'xbm',
	'application/xhtml+xml': 'xhtml',
	'application/vnd.ms-excel': 'xls',
	'application/xml': 'xsl',
	'image/x-xpixmap': 'xpm',
	'application/xslt+xml': 'xslt',
	'application/vnd.mozilla.xul+xml': 'xul',
	'image/x-xwindowdump': 'xwd',
	'application/vnd.visio': 'vsd',
	'audio/x-wav': 'wav',
	'image/vnd.wap.wbmp': 'wbmp',
	'text/vnd.wap.wml': 'wml',
	'application/vnd.wap.wmlc': 'wmlc',
	'text/vnd.wap.wmlsc': 'wmls',
	'application/vnd.wap.wmlscriptc': 'wmlscriptc',
	'video/x-ms-wmv': 'wmv',
	'application/font-woff': 'woff',
	'application/font-woff2': 'woff2',
	'model/vrml': 'wrl',
	'application/wspolicy+xml': 'wspolicy',
	'application/x-compress': 'z',
	'application/zip': 'zip'
};